import { useState, useEffect } from "react";
import { Typography, Fade, Paper, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useI18n } from "react-simple-i18n";

import { GlobalSpacing } from "./styles";
import { Divider } from "../divider/divider";
import { useCustomNavigate } from "../../provider/navigate";

const Footer = () => {
	const { t } = useI18n();
	const { customNavigate } = useCustomNavigate();
	const [showSalePopup, setShowSalePopup] = useState(false);

	useEffect(() => {
		// Show popup randomly every 30-60 seconds
		const showPopupRandomly = () => {
			const randomTime = Math.floor(Math.random() * (60000 - 30000) + 30000); // Random time between 30-60 seconds
			setTimeout(() => {
				setShowSalePopup(true);
				// Hide popup after 8 seconds
				setTimeout(() => setShowSalePopup(false), 8000);
				showPopupRandomly(); // Schedule next popup
			}, randomTime)
		}

		showPopupRandomly();

		return () => {
			// Cleanup timeouts on unmount
			setShowSalePopup(false);
		}
	}, [])

	return (
		<FooterWrapper>
			<GlobalSpacing>
				<FooterContent>
					<FooterSection>
						<Typography variant="h6">
							{t('recruitment.howToUse.title')}
						</Typography>

						<FooterList>
							<FooterListItem>
								<Typography variant="body1">
									{t('recruitment.howToUse.step1')}
								</Typography>
							</FooterListItem>

							<FooterListItem>
								<Typography variant="body1">
									{t('recruitment.howToUse.step2')}
								</Typography>
							</FooterListItem>
						</FooterList>
					</FooterSection>

					<FooterSection>
						<Typography variant="h6">
							{t('common.courses')}
						</Typography>

						<FooterList>
							<FooterListItem onClick={customNavigate('/browseCourse/robots')}>
								<Typography variant="body1">
									{t('header.robots')}
								</Typography>
							</FooterListItem>
							<FooterListItem onClick={customNavigate('/browseCourse/computer_it')}>
								<Typography variant="body1">
									{t('header.computer_it')}
								</Typography>
							</FooterListItem>
							<FooterListItem onClick={customNavigate('/browseCourse/ai')}>
								<Typography variant="body1">
									{t('header.ai')}
								</Typography>
							</FooterListItem>
							<FooterListItem onClick={customNavigate('/browseCourse/radio')}>
								<Typography variant="body1">
									{t('header.radio')}
								</Typography>
							</FooterListItem>
							<FooterListItem onClick={customNavigate('/browseCourse/engineering')}>
								<Typography variant="body1">
									{t('header.engineering')}
								</Typography>
							</FooterListItem>
							<FooterListItem onClick={customNavigate('/browseCourse/english')}>
								<Typography variant="body1">
									{t('header.english')}
								</Typography>
							</FooterListItem>
						</FooterList>
					</FooterSection>

					<FooterSection>
						<Typography variant="h6">
							{t('common.company')}
						</Typography>

						<FooterList>
							<FooterListItem onClick={customNavigate('/product')}>
								<Typography variant="body1">
									{t('product.ourProducts')}
								</Typography>
							</FooterListItem>

							<FooterListItem onClick={customNavigate('/about')}>
								<Typography variant="body1">
									{t('header.about')}
								</Typography>
							</FooterListItem>

							<FooterListItem onClick={customNavigate('/contact')}>
								<Typography variant="body1">
									{t('header.contact')}
								</Typography>
							</FooterListItem>

							<FooterListItem onClick={customNavigate('/terms')}>
								<Typography variant="body1">
									{t('signup.terms')}
								</Typography>
							</FooterListItem>

							<FooterListItem onClick={customNavigate('/privacy')}>
								<Typography variant="body1">
									{t('signup.policy')}
								</Typography>
							</FooterListItem>
						</FooterList>
					</FooterSection>
				</FooterContent>
			</GlobalSpacing>

			<Divider />

			<GlobalSpacing>
				<CopyRight variant="body1">
					©Gens nexus platform. All Rights Reserved.
				</CopyRight>
			</GlobalSpacing>

			<Fade in={showSalePopup} timeout={500}>
				<SalePopup elevation={3}>
					<Typography variant="h6" component="div">
						💰 {t('recruitment.title')}
					</Typography>
					<Box sx={{ mt: 1, mb: 1 }}>
						<Typography variant="body2" sx={{ mb: 0.5 }}>
							{t('recruitment.subTitle')}
						</Typography>
						<Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
							{t('recruitment.payment.title')}
						</Typography>
						<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
							{t('recruitment.payment.method1')}
						</Typography>
						<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
							{t('recruitment.payment.method2')}
						</Typography>
					</Box>
				</SalePopup>
			</Fade>
		</FooterWrapper>
	);
};

const FooterWrapper = styled(Box)(({ theme }) => ({
	position: 'relative',
	display: "flex",
	flexDirection: "column",
	backgroundColor: theme.palette.common.footerBg,
	// marginTop: theme.spacing(6),

	// [theme.breakpoints.down("md")]: {
	// 	marginTop: theme.spacing(3),
	// },
}))

const FooterContent = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	padding: theme.spacing(4, 0),
	gap: theme.spacing(4),
	flexWrap: 'wrap',

	[theme.breakpoints.down('md')]: {
		flexDirection: 'column',
		padding: theme.spacing(3, 0),
		gap: theme.spacing(3),
	}
}))

const FooterSection = styled(Box)(({ theme }) => ({
	flex: '1 1 200px',
	minWidth: '200px',
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),

	h6: {
		fontWeight: 600,
		color: theme.palette.common.secondColor,
	},

	[theme.breakpoints.down('md')]: {
		gap: theme.spacing(1),
	}
}))

const FooterList = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(1),
}))

const FooterListItem = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1),
	cursor: 'pointer',
	transition: 'color 0.2s ease',

	'& p': {
		color: alpha(theme.palette.common.secondColor, 0.75),
		fontSize: '0.95rem',
	},

	'&:hover p': {
		color: theme.palette.common.secondColor,
	}
}))

const CopyRight = styled(Typography)(({ theme }) => ({
	color: theme.palette.common.secondColor,
	padding: theme.spacing(3, 0),
	fontWeight: 400,

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(2, 0),
	},
}))

const SalePopup = styled(Paper)(({ theme }) => ({
	position: 'fixed',
	bottom: theme.spacing(12),
	right: theme.spacing(3),
	padding: theme.spacing(2),
	borderRadius: theme.spacing(2),
	background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${alpha(theme.palette.common.white, 0.95)} 100%)`,
	boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
	zIndex: 1000,
	textAlign: 'left',
	cursor: 'pointer',
	transition: 'transform 0.3s ease',
	maxWidth: '300px',

	'&:hover': {
		transform: 'translateY(-5px)',
	},

	'h6': {
		color: theme.palette.common.firstColor,
		fontWeight: 600,
		marginBottom: theme.spacing(1),
		textAlign: 'center',
	},

	'p': {
		color: alpha(theme.palette.common.firstColor, 0.8),
	},

	[theme.breakpoints.down("sm")]: {
		bottom: theme.spacing(8),
		right: theme.spacing(2),
		padding: theme.spacing(1.5),
		maxWidth: '250px',
	}
}))

export { Footer };